// Auth.tsx
import React, { useState } from 'react';
import { getSupabase } from '../../lib/api/supabase';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { ensureDefaultTemplateExists } from '../../lib/categories/defaultTemplate';
import LogRocket from 'logrocket'; // Add this import
import Logo from '../../assets/ProvafiDarkText.svg';

interface AuthProps {
  onAuthSuccess: () => void;
  onBetaInquiryClick?: () => void; // Add this prop
  onGeneralInquiryClick?: () => void;
}

// Import missing components (you'll need to create these)
import Terms from '../legal/Terms';
import Privacy from '../legal/Privacy';

type AuthView = 'login' | 'signup' | 'forgot-password' | 'reset-sent' | 'confirmation-sent' | 'verification-success' | 'terms' | 'privacy';

// Add at the top of your file, near other constants
const BETA_MODE = true; // Toggle this to false to disable beta mode
const BETA_CODES = ['PROVAFI2024', 'HNFREE']; // Add multiple codes if needed

// Get URL parameters
const getUrlParameter = (name: string): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
};

export function Auth({ onAuthSuccess, onBetaInquiryClick, onGeneralInquiryClick }: AuthProps) {
  const [view, setView] = useState<AuthView>(() => {
    // Check URL parameters for verification status
    const access_token = getUrlParameter('access_token');
    const refresh_token = getUrlParameter('refresh_token');
    const verified = getUrlParameter('verified');
    const showSignup = getUrlParameter('signup') === 'true';
    
    // If we have tokens, we should process them
    if (access_token && refresh_token) {
      // Process the tokens
      const supabase = getSupabase();
      supabase.auth.setSession({
        access_token,
        refresh_token
      }).then(({ error }) => {
        if (!error) {
          onAuthSuccess();
        }
      });
    }
    
    // Show verification success if that's what we're here for
    if (verified === 'true') return 'verification-success';
    // Show signup if that's specified in URL
    if (showSignup) return 'signup';
    // Default to login
    return 'login';
  });
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  
  // Add new state for terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Add new state for beta code
  const [betaCode, setBetaCode] = useState(() => {
    const urlBetaCode = getUrlParameter('beta');
    return urlBetaCode || '';
  });

  // Add new function to handle terms/privacy view navigation
  const handleViewTerms = (type: 'terms' | 'privacy') => {
    setView(type);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabase();
            
      if (view === 'forgot-password') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });
        
        if (resetError) throw resetError;
        
        setView('reset-sent');
        setMessage('Check your email for the password reset link. The link will expire in 24 hours.');
      } else if (view === 'signup') {
        // Validate terms acceptance
        if (!termsAccepted) {
          setError('You must accept the Terms and Privacy Policy to create an account');
          setLoading(false);
          return;
        }
        // Add beta code validation
        if (BETA_MODE && !BETA_CODES.includes(betaCode)) {
          setError('Invalid beta access code');
          setLoading(false);
          return;
        }
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name,
              terms_accepted: true,
              terms_accepted_at: new Date().toISOString(),
              // Optionally track beta users
              beta_user: BETA_MODE ? true : undefined,
              beta_code_used: BETA_MODE ? betaCode : undefined
            },
            emailRedirectTo: `${window.location.origin}/auth/callback` // Use dynamic origin
          }
        });
      
        // Check for existing user
        if (data?.user?.identities?.length === 0) {
          setError('An account with this email already exists. Please sign in or use a different email.');
          setView('login');
          setLoading(false);
          return;
        }
            
        if (signUpError) {
          throw signUpError;
        }

        // Create default template for new user
        if (data?.user) {
          await ensureDefaultTemplateExists(data.user.id);

          // Add LogRocket identification here for signup
          if (process.env.NODE_ENV === 'production') {
            LogRocket.identify(data.user.id, {
              email: data.user.email,
              name: name,
              betaUser: BETA_MODE ? true : false
            });
          }
        }
        
        // Add metadata check after signup, regardless of error
        try {
          const { data: { user } } = await supabase.auth.getUser();
          console.log('User metadata:', user?.user_metadata);
        } catch (err) {
          console.error('Error checking user metadata:', err);
        }
        
        setView('confirmation-sent');
        setMessage('Please check your email to verify your account. Click the link in the email to complete your registration.');
      } else {
        const { error: signInError, data } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) throw signInError;

        // Ensure default template exists for existing users too
        if (data?.user) {
          await ensureDefaultTemplateExists(data.user.id);

          // Add LogRocket identification here for login
          if (process.env.NODE_ENV === 'production') {
            LogRocket.identify(data.user.id, {
              email: data.user.email,
              // You can add any additional user properties you want to track
              betaUser: data.user.user_metadata?.beta_user || false
            });
          }
        }
        onAuthSuccess();
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Terms and Privacy views
  if (view === 'terms') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Terms />
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setView('signup')}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Sign Up
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'privacy') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Privacy />
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setView('signup')}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Sign Up
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Terms checkbox render function
  const renderTermsCheckbox = () => (
    <div className="flex items-center space-x-2">
      <input
        type="checkbox"
        id="terms-accept"
        checked={termsAccepted}
        onChange={(e) => setTermsAccepted(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <label htmlFor="terms-accept" className="text-sm text-gray-600">
        I accept the{' '}
        <button
          type="button"
          onClick={() => handleViewTerms('terms')}
          className="text-blue-600 hover:underline"
        >
          Terms of Service
        </button>{' '}
        and{' '}
        <button
          type="button"
          onClick={() => handleViewTerms('privacy')}
          className="text-blue-600 hover:underline"
        >
          Privacy Policy
        </button>
      </label>
    </div>
  );
  
  // Updated main auth form with new header styling
  const renderMainContent = () => (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          <a href="https://provafi.com" className="inline-block hover:opacity-90 transition-opacity">
            <div className="flex flex-col items-center">
              <img
                src={Logo}
                alt="Provafi"
                className="h-12 mx-auto"
              />
            </div>
          </a>
        </h1>
        <h2 className="text-xl text-gray-600 mb-8">
          AI-Powered Quality of Earnings Insights Without the Wait
        </h2>
        
        {getUrlParameter('verified') === 'true' && (
          <Alert className="mb-8">
            <AlertTitle>Email Verified Successfully</AlertTitle>
            <AlertDescription>
              You can now sign in to your account.
            </AlertDescription>
          </Alert>
        )}
      </div>
  
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="rounded-md bg-red-50 p-4 mb-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          {view === 'reset-sent' && renderResetSent()}
          {view === 'confirmation-sent' && renderConfirmationSent()}
          {view === 'verification-success' && renderVerificationSuccess()}
          {view === 'forgot-password' && renderForgotPassword()}
          {(view === 'login' || view === 'signup') && renderAuthForm()}
        </div>
      </div>
    </div>
  );
  
  // Your existing render functions remain the same, just update the auth form to include beta inquiry button
  const renderAuthForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {view === 'signup' && BETA_MODE && (
        <div className="rounded-md bg-blue-50 p-3 mb-4">
          <p className="text-sm text-blue-700">
            Provafi accounts are currently available to approved clients. Access is granted after invoice payment.
          </p>
        </div>
      )}
      {view === 'signup' && (
        <>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <div className="mt-1">
              <input
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
          {/* Add signup code field - will only show if BETA_MODE is true */}
          {BETA_MODE && (
            <div>
              <label htmlFor="signup-code" className="block text-sm font-medium text-gray-700">
                Signup Code
              </label>
              <div className="mt-1">
                <input
                  id="signup-code"
                  name="signup-code"
                  type="text"
                  required
                  value={betaCode}
                  onChange={(e) => setBetaCode(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your signup code"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                You'll receive your signup code after invoice payment.
              </p>
            </div>
          )}
          
        </>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <div className="mt-1">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <div className="mt-1">
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Moved Terms and Privacy Policy checkbox to appear after the password field and before the submit button */}
      {view === 'signup' && renderTermsCheckbox()}

      <div>
        <button
          type="submit"
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {loading ? 'Loading...' : view === 'signup' ? 'Sign up' : 'Sign in'}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setView(view === 'signup' ? 'login' : 'signup')}
          className="text-sm text-blue-600 hover:text-blue-500"
        >
          {view === 'signup' ? 'Already have an account? Sign in' : 'Have a signup code? Complete signup'}
        </button>
        {view === 'login' && (
          <button
            type="button"
            onClick={() => setView('forgot-password')}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            Forgot password?
          </button>
        )}
      </div>

      {/* Add general inquiry button without the "Or" separator */}
      {onGeneralInquiryClick && (
        <div className="pt-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
          </div>

          <div className="mt-4">
            <button
              type="button"
              onClick={onGeneralInquiryClick}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Contact us
            </button>
          </div>
        </div>
      )}

      {/* Add "Don't have access yet?" link for login view */}
      {view === 'login' && BETA_MODE && (
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have access yet?{' '}
            <a
              href="https://calendar.app.google/FHKAqGEBVnFWRn356"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-500 font-medium"
            >
              Book a demo
            </a>
          </p>
        </div>
      )}
    </form>
  );

  const renderVerificationSuccess = () => (
    <div className="text-center">
      <div className="rounded-md bg-green-50 p-4">
        <div className="text-sm text-green-700">
          Your email has been verified successfully! You can now sign in to your account.
        </div>
      </div>
      <button
        onClick={() => setView('login')}
        className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-500"
      >
        Sign in
      </button>
    </div>
  );

  const renderForgotPassword = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-center text-sm text-gray-600">
        Enter your email address and we'll send you a link to reset your password.
      </p>
      <div>
        <label htmlFor="email-address" className="sr-only">
          Email address
        </label>
        <input
          id="email-address"
          name="email"
          type="email"
          autoComplete="email"
          required
          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {loading ? 'Sending...' : 'Send reset link'}
        </button>
      </div>
      <div className="text-center">
        <button
          type="button"
          onClick={() => setView('login')}
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Back to sign in
        </button>
      </div>
    </form>
  );

  const renderResetSent = () => (
    <div className="text-center">
      <div className="rounded-md bg-green-50 p-4">
        <div className="text-sm text-green-700">
          {message}
        </div>
      </div>
      <button
        onClick={() => setView('login')}
        className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-500"
      >
        Back to sign in
      </button>
    </div>
  );

  const renderConfirmationSent = () => (
    <div className="text-center">
      <div className="rounded-md bg-green-50 p-4">
        <div className="text-sm text-green-700">
          {message}
        </div>
      </div>
      <button
        onClick={() => setView('login')}
        className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-500"
      >
        Back to sign in
      </button>
    </div>
  );

  return renderMainContent();
}

export default Auth;
